/**
 * useSalesFunnelChannelRoiSummary — Issue #397.
 *
 * Calls the sales funnel + channel ROI summary report API via @jinzhuan/api-client.
 * No frontend fake state; all data from backend.
 *
 * Red lines:
 *   - Must use @jinzhuan/api-client; no hand-written fetch / axios for JSON.
 *   - Money / ROI / commission strings are kept as Decimal-safe strings; do not
 *     parseFloat / Number / toFixed on them. Use decimal.js for any local math.
 *   - Status fields use string dictionaries (`connected` / `partial` /
 *     `pending_source` / `not_connected`); render as controlled labels — never
 *     coerce missing data into "0".
 */
import { useCallback, useState } from 'react';
import {
  createApiClient,
  ApiError as ApiClientError,
  type ApiComponents,
} from '@/lib/adminApiClient';
import { getStoredAccessToken } from '@/lib/tokenStorage';

const apiClient = createApiClient({
  baseUrl: '',
  getAccessToken: () => getStoredAccessToken(),
});

export type SalesFunnelChannelRoiSummaryBatchSummaryDto =
  ApiComponents['schemas']['SalesFunnelChannelRoiSummaryBatchSummaryDto'];
export type SalesFunnelChannelRoiSummaryBatchDetailDto =
  ApiComponents['schemas']['SalesFunnelChannelRoiSummaryBatchDetailDto'];
export type SalesFunnelChannelRoiSummaryBatchListResponseDto =
  ApiComponents['schemas']['SalesFunnelChannelRoiSummaryBatchListResponseDto'];
export type GenerateSalesFunnelChannelRoiSummaryDto =
  ApiComponents['schemas']['GenerateSalesFunnelChannelRoiSummaryDto'];
export type ReviewSalesFunnelChannelRoiSummaryDto =
  ApiComponents['schemas']['ReviewSalesFunnelChannelRoiSummaryDto'];
export type PublishSalesFunnelChannelRoiSummaryDto =
  ApiComponents['schemas']['PublishSalesFunnelChannelRoiSummaryDto'];
export type SalesFunnelChannelRoiSummarySourceStatusResponseDto =
  ApiComponents['schemas']['SalesFunnelChannelRoiSummarySourceStatusResponseDto'];

export interface ApiError {
  message: string;
  code?: string;
}

function toApiError(err: unknown): ApiError {
  if (err instanceof ApiClientError) {
    if (err.status === 401) return { message: '会话已失效', code: 'UNAUTHENTICATED' };
    if (err.status === 403) return { message: '没有权限', code: 'FORBIDDEN' };
    if (err.status === 404) return { message: '记录不存在', code: 'NOT_FOUND' };
    if (err.status === 409) return { message: '数据冲突，请刷新重试', code: 'CONFLICT' };
  }
  if (err instanceof Error) return { message: err.message };
  return { message: String(err) };
}

export type SalesFunnelChannelRoiSummaryBatchStatus =
  | 'draft'
  | 'generated'
  | 'finance_reviewed'
  | 'published'
  | 'archived';

export interface ListFilter {
  status?: SalesFunnelChannelRoiSummaryBatchStatus;
  periodYear?: number;
  periodMonth?: number;
  page?: number;
  limit?: number;
}

const BATCH_STATUSES: ReadonlySet<SalesFunnelChannelRoiSummaryBatchStatus> =
  new Set(['draft', 'generated', 'finance_reviewed', 'published', 'archived']);

export function coerceBatchStatus(
  raw: string | undefined,
): SalesFunnelChannelRoiSummaryBatchStatus | undefined {
  if (!raw) return undefined;
  return BATCH_STATUSES.has(raw as SalesFunnelChannelRoiSummaryBatchStatus)
    ? (raw as SalesFunnelChannelRoiSummaryBatchStatus)
    : undefined;
}

export type SalesFunnelChannelRoiSourceType =
  | 'natural_visit'
  | 'referral'
  | 'channel_partner'
  | 'repurchase'
  | 'marketing_event'
  | 'online_lead'
  | 'unknown';

const SOURCE_TYPES: ReadonlySet<SalesFunnelChannelRoiSourceType> = new Set([
  'natural_visit',
  'referral',
  'channel_partner',
  'repurchase',
  'marketing_event',
  'online_lead',
  'unknown',
]);

export function coerceSourceType(
  raw: string | undefined,
): SalesFunnelChannelRoiSourceType | undefined {
  if (!raw) return undefined;
  return SOURCE_TYPES.has(raw as SalesFunnelChannelRoiSourceType)
    ? (raw as SalesFunnelChannelRoiSourceType)
    : undefined;
}

export type DetailQuery = {
  channelPartnerId?: string;
  marketTeamId?: string;
  // Plain string at the page level; narrowed via coerceSourceType before
  // forwarding to the typed api-client.
  sourceType?: string;
};

// ─── List / detail / source-status ───────────────────────────────────────────

export function useSalesFunnelChannelRoiSummaryBatches() {
  const [data, setData] =
    useState<SalesFunnelChannelRoiSummaryBatchListResponseDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const fetchList = useCallback(async (filter: ListFilter = {}) => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiClient.GET(
        '/api/v1/reports/sales-funnel-channel-roi-summary',
        {
          params: {
            query: {
              status: filter.status,
              periodYear: filter.periodYear,
              periodMonth: filter.periodMonth,
              page: filter.page,
              limit: filter.limit,
            },
          },
        },
      );
      setData(result.data ?? null);
    } catch (err) {
      setError(toApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  return { data, loading, error, fetchList };
}

export function useSalesFunnelChannelRoiSummaryBatchDetail() {
  const [data, setData] =
    useState<SalesFunnelChannelRoiSummaryBatchDetailDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const fetchDetail = useCallback(
    async (id: string, query: DetailQuery = {}) => {
      setLoading(true);
      setError(null);
      try {
        const result = await apiClient.GET(
          '/api/v1/reports/sales-funnel-channel-roi-summary/{id}',
          {
            params: {
              path: { id },
              query: {
                channelPartnerId: query.channelPartnerId,
                marketTeamId: query.marketTeamId,
                sourceType: coerceSourceType(query.sourceType),
              },
            },
          },
        );
        setData(result.data ?? null);
      } catch (err) {
        setError(toApiError(err));
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { data, loading, error, fetchDetail };
}

export function useSalesFunnelChannelRoiSummarySourceStatus() {
  const [data, setData] =
    useState<SalesFunnelChannelRoiSummarySourceStatusResponseDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const fetchSourceStatus = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiClient.GET(
        '/api/v1/reports/sales-funnel-channel-roi-summary/{id}/source-status',
        { params: { path: { id } } },
      );
      setData(result.data ?? null);
    } catch (err) {
      setError(toApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  return { data, loading, error, fetchSourceStatus };
}

// ─── Actions ───────────────────────────────────────────────────────────────────

export function useSalesFunnelChannelRoiSummaryActions() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const generate = useCallback(
    async (
      dto: GenerateSalesFunnelChannelRoiSummaryDto,
    ): Promise<SalesFunnelChannelRoiSummaryBatchSummaryDto | null> => {
      setLoading(true);
      setError(null);
      try {
        const result = await apiClient.POST(
          '/api/v1/reports/sales-funnel-channel-roi-summary/generate',
          { body: dto },
        );
        return result.data ?? null;
      } catch (err) {
        setError(toApiError(err));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const review = useCallback(
    async (
      id: string,
      dto: ReviewSalesFunnelChannelRoiSummaryDto,
    ): Promise<SalesFunnelChannelRoiSummaryBatchSummaryDto | null> => {
      setLoading(true);
      setError(null);
      try {
        const result = await apiClient.POST(
          '/api/v1/reports/sales-funnel-channel-roi-summary/{id}/review',
          { params: { path: { id } }, body: dto },
        );
        return result.data ?? null;
      } catch (err) {
        setError(toApiError(err));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const publish = useCallback(
    async (
      id: string,
      dto: PublishSalesFunnelChannelRoiSummaryDto,
    ): Promise<SalesFunnelChannelRoiSummaryBatchSummaryDto | null> => {
      setLoading(true);
      setError(null);
      try {
        const result = await apiClient.POST(
          '/api/v1/reports/sales-funnel-channel-roi-summary/{id}/publish',
          { params: { path: { id } }, body: dto },
        );
        return result.data ?? null;
      } catch (err) {
        setError(toApiError(err));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { loading, error, generate, review, publish };
}
